/**
 * Frecency (PLAN §3.4): every visit adds 100, decaying with a 14-day
 * half-life (Firefox / Raycast). Stored LOCALLY per account in localStorage
 * under `apibooKey('palette-frecency')` as `{ v: 1, items: { "<ref>": [score, lastTs] } }`,
 * capped at 1,000 entries (the lowest are evicted), written debounced. Never
 * synced; cleared by "Clear recent" and on sign-out-and-forget.
 *
 * A parse failure resets the store (R8). The stored score is the value AT
 * `lastTs`; reads decay it to `now`.
 */
import { apibooKey } from '../services/storageNamespace';

export const HALF_LIFE_MS = 14 * 24 * 60 * 60 * 1000;
export const VISIT_POINTS = 100;
export const MAX_ENTRIES = 1000;
export const WRITE_DEBOUNCE_MS = 400;
/** The decayed score that maps to frecency 1.0 (≈ ten recent visits). */
const SATURATION = 1000;
const VERSION = 1;

let items = null; // Map<ref, [score, lastTs]>
let loadedKey = null;
let writeTimer = null;
const listeners = new Set();

function storageKey() {
  try {
    return apibooKey('palette-frecency');
  } catch {
    return 'apiboo.palette-frecency';
  }
}

function load() {
  const key = storageKey();
  if (items && loadedKey === key) return items;
  items = new Map();
  loadedKey = key;
  try {
    const raw = localStorage.getItem(key);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.v === VERSION && parsed.items && typeof parsed.items === 'object') {
        for (const [ref, v] of Object.entries(parsed.items)) {
          if (Array.isArray(v) && Number.isFinite(v[0]) && Number.isFinite(v[1])) items.set(ref, [v[0], v[1]]);
        }
      }
    }
  } catch {
    items = new Map();
  }
  return items;
}

function decayed([score, lastTs], now) {
  const age = Math.max(0, now - lastTs);
  return score * 2 ** (-age / HALF_LIFE_MS);
}

function evict(map, now) {
  if (map.size <= MAX_ENTRIES) return;
  const sorted = [...map.entries()].sort((a, b) => decayed(a[1], now) - decayed(b[1], now));
  for (const [ref] of sorted.slice(0, map.size - MAX_ENTRIES)) map.delete(ref);
}

function persistNow() {
  writeTimer = null;
  if (!items) return;
  try {
    const out = {};
    for (const [ref, v] of items) out[ref] = [Math.round(v[0] * 1000) / 1000, v[1]];
    localStorage.setItem(loadedKey || storageKey(), JSON.stringify({ v: VERSION, items: out }));
  } catch {
    /* quota / private mode: frecency is a convenience */
  }
}

function schedule() {
  if (writeTimer) clearTimeout(writeTimer);
  writeTimer = setTimeout(persistNow, WRITE_DEBOUNCE_MS);
  for (const fn of [...listeners]) {
    try {
      fn();
    } catch {
      /* ignore */
    }
  }
}

/** @param {string} ref `${provider}:${id}` */
export function recordVisit(ref, at = Date.now()) {
  if (!ref || typeof ref !== 'string' || ref.indexOf(':') <= 0) return;
  const map = load();
  const prev = map.get(ref);
  const base = prev ? decayed(prev, at) : 0;
  map.delete(ref); // re-insert: Map order = recency order for ties
  map.set(ref, [base + VISIT_POINTS, at]);
  evict(map, at);
  schedule();
}

/** 0..1 */
export function frecencyOf(ref, now = Date.now()) {
  const v = load().get(ref);
  if (!v) return 0;
  return Math.min(1, decayed(v, now) / SATURATION);
}

/** The n most recently visited refs, newest first. */
export function recentRefs(n) {
  const map = load();
  return [...map.entries()]
    .sort((a, b) => b[1][1] - a[1][1])
    .slice(0, Math.max(0, n | 0))
    .map(([ref]) => ref);
}

export function forget(ref) {
  const map = load();
  if (map.delete(ref)) schedule();
}

export function clearRecent() {
  const map = load();
  map.clear();
  schedule();
  persistNow();
}

/** Notified on every change (Recent re-renders). */
export function subscribeFrecency(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Flush a pending write now (sign-out, tests). */
export function flushFrecency() {
  if (writeTimer) {
    clearTimeout(writeTimer);
    persistNow();
  }
}

/** Drop the in-memory copy (account switch, tests); the next read reloads. */
export function resetFrecencyCache() {
  if (writeTimer) clearTimeout(writeTimer);
  writeTimer = null;
  items = null;
  loadedKey = null;
}
